import React, { FC } from 'react';
import { GetStaticProps, GetStaticPropsContext } from 'next';
import { DynamicComponent } from '@/components';
import { TypeHomePage, TypeRedesignHomepage } from '@/core/generated-types';
import { loadLayout } from '@/utils/loadLayout';
import { ContentfulPreviewData, getEntries } from '@/lib/contentful';
import { BackToTop } from '@/components/common/BackToTop';
import { Seo } from '@/components/ui/Seo';
import {
  buildCanonicalUrl,
  defaultCacheRevalidation,
} from '@/core/contentfulConfig';
import { locales } from '@/core/constants';
import HomepageUS from '@/components/homepage-us/page';

export interface Props {
  title: string;
  page: TypeHomePage;
  redesignPage: TypeRedesignHomepage;
  isHomepageRedesignExperimentEnable: boolean;
}

export const getStaticPaths = () => {
  const paths = locales.map((locale) => ({
    params: {
      slug: '/',
      locale,
    },
  }));
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({
  preview = false,
  previewData,
  params,
}: GetStaticPropsContext) => {
  const { locale } = params as { locale: string };
  const previewDataPayload = previewData as ContentfulPreviewData;
  const page = await getEntries(
    {
      slug: 'home',
      locale,
      contentType: 'homePage',
    },
    preview,
    previewDataPayload,
  );

  const redesignPage = await getEntries(
    {
      slug: 'redesign-homepage-experiment-homepage',
      locale,
      contentType: 'redesignHomepage',
    },
    preview,
    previewDataPayload,
  );

  const { header, footer, footerLegal } = await loadLayout({
    locale,
    isPreview: preview,
    previewData: previewDataPayload,
  });

  if (page?.items.length === 0 || redesignPage?.items.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page: page?.items?.[0] || null,
      redesignPage: redesignPage?.items?.[0] || null,
      header,
      footer,
      footerLegal,
    },
    revalidate: defaultCacheRevalidation,
  };
};

const Home: FC<Props> = ({ page, redesignPage }) => {
  const {
    fields: { heroSection, components, metadata },
  } = page;

  const redesignPageFields = redesignPage?.fields;
  const region = process.env.NEXT_PUBLIC_REGION?.toLocaleLowerCase() ?? '';
  const isUSRegion = region === 'us';
  const metadataFields = isUSRegion
    ? redesignPageFields.metadata.fields
    : metadata?.fields;

  return (
    <>
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
      />
      {isUSRegion ? (
        <HomepageUS redesignPageFields={redesignPageFields} />
      ) : (
        <>
          <DynamicComponent component={heroSection} />
          {components &&
            components.map((component, i) => (
              <DynamicComponent
                key={`${component.sys.id}-${i}`}
                component={component}
              />
            ))}
        </>
      )}
      <BackToTop />
    </>
  );
};

export default Home;
